// A beat that plays while sound is on (the footer's Sound toggle,
// localStorage 'user-sound'). Synthesised with Web Audio, so there is no file
// to download, and all through a low-pass so it sits under the page. Beats
// only, no melodies: drums, bass and chords in three styles, on the same
// beat grid. It opens with lo-fi and loops it: the player's Auto/Hold
// toggle starts on Hold. With Auto on, each plays for sixteen bars, then over
// four bars the next fades in, the last fades out, and the tempo glides from
// one to the other, so there's never a stop or a jump (or, in cut mode, it
// swaps straight over), in this order:
//   lofi       84 bpm: soft kick, brushed snare, swung hats, Rhodes sevenths
//   house      120 bpm: four on the floor, open hats, off-beat bass, stabs
//   dancehall  96 bpm: the dembow (claps on the 3-3-2) and a sub bass
//
// While it plays, <html> carries .beat-on and --beat (one beat, in seconds),
// which is what HomeAvatar nods to, and each beat fires a 'chillbeat' event
// on window (detail.beat counts from 0, detail.beatMs is its length) as it
// sounds, for the moves that land on a count. Turning the sound off fires
// 'chillbeat-stop' and then removes the class, so the dancing stops with the
// music, and a listener can still read the pose it stopped in.
// getBeatState(), blendTo() and setAuto() are for the test player
// (BeatPlayer), which listens for 'chillbeat-state'.

const LOOKAHEAD = 0.12; // seconds of audio scheduled ahead of the clock
const SOLO_BARS = 16; // each track on its own
const BLEND_BARS = 4; // the crossfade and tempo glide between them
const TICK_MS = 25;

interface Track {
  name: string;
  label: string;
  bpm: number;
  swing: number; // how late the off-sixteenths land, as a share of a step
  play: (inBar: number, bar: number, t: number) => void;
}

const AudioCtx: typeof AudioContext | undefined =
  typeof window !== 'undefined'
    ? window.AudioContext || (window as any).webkitAudioContext
    : undefined;

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let noise: AudioBuffer | null = null;
let echo: GainNode | null = null;
let echoDelay: DelayNode | null = null;
let order: Track[] = [];
// The blend: the track playing, the one it's blending into (if any), from
// which step, when the next automatic blend is due, and one asked for.
let current: Track;
let target: Track | null = null;
let blendStart = 0;
let nextAuto = 0;
let auto = false; // Hold: loop the current track until Auto is turned on
let blending = true; // false cuts straight to the next track instead
let requested: Track | null = null;
let lastMix = 0;
let beatSec = 0.7;
// Where the instruments play into: each track has its own bus, faded
// against the other's during a blend.
let out: AudioNode | null = null;
let outLevel = 1; // that bus's level, which the echo send follows
const buses = new Map<Track, GainNode>();
let timer: number | null = null;
let nextTime = 0;
let step = 0;
let playing = false;
let paused = false; // stopped from the player, with the sound still on

const hz = (midi: number) => 440 * Math.pow(2, (midi - 69) / 12);

function setup() {
  if (!AudioCtx) return false;
  if (!ctx) {
    ctx = new AudioCtx();
    const tone = ctx.createBiquadFilter();
    tone.type = 'lowpass';
    tone.frequency.value = 3200;
    master = ctx.createGain();
    master.gain.value = 0;
    master.connect(tone);
    tone.connect(ctx.destination);

    // A dub echo the stabs can be sent into: a delay feeding back into
    // itself through a low-pass, so each repeat is darker than the last.
    echo = ctx.createGain();
    echoDelay = ctx.createDelay(2);
    const feedback = ctx.createGain();
    feedback.gain.value = 0.38;
    const darken = ctx.createBiquadFilter();
    darken.type = 'lowpass';
    darken.frequency.value = 1800;
    echo.connect(echoDelay);
    echoDelay.connect(darken).connect(feedback).connect(echoDelay);
    darken.connect(master);

    noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = noise.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  }
  return true;
}

function kick(t: number, from = 110, to = 42, level = 0.9) {
  const osc = ctx!.createOscillator();
  const g = ctx!.createGain();
  osc.frequency.setValueAtTime(from, t);
  osc.frequency.exponentialRampToValueAtTime(to, t + 0.14);
  g.gain.setValueAtTime(level, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
  osc.connect(g).connect(out!);
  osc.start(t);
  osc.stop(t + 0.42);
}

function hiss(t: number, type: BiquadFilterType, freq: number, level: number, length: number) {
  const src = ctx!.createBufferSource();
  src.buffer = noise;
  const f = ctx!.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  const g = ctx!.createGain();
  g.gain.setValueAtTime(level, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + length);
  src.connect(f).connect(g).connect(out!);
  src.start(t, Math.random() * 0.5);
  src.stop(t + length + 0.02);
}

function note(t: number, midi: number, level: number, length: number, type: OscillatorType) {
  const g = ctx!.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(level, t + 0.04);
  g.gain.exponentialRampToValueAtTime(0.0001, t + length);
  // Two slightly detuned voices, for the warble of an old keyboard.
  for (const detune of [-6, 6]) {
    const osc = ctx!.createOscillator();
    osc.type = type;
    osc.frequency.value = hz(midi);
    osc.detune.value = detune;
    osc.connect(g);
    osc.start(t);
    osc.stop(t + length + 0.05);
  }
  g.connect(out!);
}

// A short, bright stab: the dancehall riff, the ska chop and horns. Square
// waves (unless told otherwise) through a band-pass, with an optional send
// into the dub echo.
function stab(
  t: number,
  notes: number[],
  level: number,
  length: number,
  freq: number,
  send = 0,
  type: OscillatorType = 'square',
) {
  const f = ctx!.createBiquadFilter();
  f.type = 'bandpass';
  f.frequency.value = freq;
  f.Q.value = 0.8;
  const g = ctx!.createGain();
  g.gain.setValueAtTime(level, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + length);
  for (const m of notes) {
    const osc = ctx!.createOscillator();
    osc.type = type;
    osc.frequency.value = hz(m);
    osc.connect(f);
    osc.start(t);
    osc.stop(t + length + 0.02);
  }
  f.connect(g).connect(out!);
  if (send) {
    const s = ctx!.createGain();
    s.gain.value = send * outLevel;
    g.connect(s).connect(echo!);
  }
}

const beat = () => beatSec;

// Fmaj7, Em7, Dm7, Cmaj7, voiced around middle C.
const LOFI_CHORDS = [
  [53, 57, 60, 64],
  [52, 55, 59, 62],
  [50, 53, 57, 60],
  [48, 52, 55, 59],
];
const LOFI_BASS = [41, 40, 38, 36];
const lofi: Track = {
  name: 'lofi',
  label: 'Lo-fi',
  bpm: 84,
  swing: 0.18,
  play(inBar, bar, t) {
    const b = bar % 4;
    if (inBar === 0) {
      for (const m of LOFI_CHORDS[b]) note(t, m, 0.05, beat() * 3.8, 'triangle');
      note(t, LOFI_BASS[b], 0.16, beat() * 1.8, 'sine');
    }
    if (inBar === 10) note(t, LOFI_BASS[b] + 7, 0.1, beat() * 1.2, 'sine');
    if ([0, 7, 10].includes(inBar)) kick(t);
    if (inBar === 4 || inBar === 12) hiss(t, 'bandpass', 1800, 0.22, 0.2);
    // Every fourth bar ends on a little fill: three ghost snares into the
    // turnaround, and an open hat in place of the last closed one.
    const fill = b === 3;
    if (fill && inBar >= 13) hiss(t, 'bandpass', 2200, 0.06 + (inBar - 13) * 0.04, 0.12);
    if (fill && inBar === 14) hiss(t, 'highpass', 6000, 0.05, 0.3);
    else if ([0, 2, 4, 6, 8, 10, 12, 14, 15].includes(inBar)) {
      hiss(t, 'highpass', 7000, inBar % 4 === 0 ? 0.05 : 0.025, 0.05);
    }
  },
};

const clap = (t: number, level: number) => {
  // Two hits a hair apart, the way hands never quite meet at once.
  hiss(t, 'bandpass', 1500, level, 0.09);
  hiss(t + 0.012, 'bandpass', 1300, level * 0.7, 0.12);
};

// Gm and Eb, two bars each.
const DH_ROOT = [43, 43, 39, 39];
const DH_CHORD = [
  [55, 58, 62],
  [55, 58, 62],
  [51, 55, 58],
  [51, 55, 58],
];
const dancehall: Track = {
  name: 'dancehall',
  label: 'Dancehall',
  bpm: 96,
  swing: 0.04,
  play(inBar, bar, t) {
    const b = bar % 4;
    const root = DH_ROOT[b];
    // Kick on the beat, the off ones lighter.
    if (inBar % 4 === 0) kick(t, 120, 40, inBar % 8 === 0 ? 0.95 : 0.6);
    // The dembow: claps on the 3-3-2 of each half bar.
    if ([3, 6, 11, 14].includes(inBar)) clap(t, 0.2);
    // A snare roll into every fourth bar.
    if (b === 3 && inBar >= 12) clap(t, 0.08 + (inBar - 12) * 0.04);
    // Sixteenth hats, the off-eighths pushed.
    hiss(t, 'highpass', 8500, inBar % 4 === 2 ? 0.04 : 0.015, 0.035);
    // Sub bass under the kick and the claps.
    if (inBar === 0) note(t, root, 0.26, beat() * 1.4, 'sine');
    if (inBar === 6) note(t, root, 0.2, beat() * 0.5, 'sine');
    if (inBar === 8) note(t, root + 3, 0.2, beat() * 0.7, 'sine');
    if (inBar === 11) note(t, root + 5, 0.18, beat() * 0.6, 'sine');
    // A chord swell under the second bar of each pair.
    if (bar % 2 === 1 && inBar === 0) {
      for (const m of DH_CHORD[b]) note(t, m, 0.025, beat() * 3.5, 'triangle');
    }
  },
};

// House: four on the floor, a clap on 2 and 4, open hats on the off-beats,
// an off-beat bass, and piano stabs through eight chords, two beats each.
const HOUSE_CHORDS = ['D', 'A', 'Bm', 'F#m', 'G', 'D', 'G', 'A'];
const house: Track = {
  name: 'house',
  label: 'House',
  bpm: 120,
  swing: 0.08,
  play(inBar, bar, t) {
    const c = CH[HOUSE_CHORDS[((bar * 2 + Math.floor(inBar / 8)) % 8)]];
    // Four on the floor.
    if (inBar % 4 === 0) kick(t, 130, 45, 0.9);
    if (inBar === 4 || inBar === 12) clap(t, 0.18);
    // Open hats on the off-beats, closed ones ticking between.
    if (inBar % 4 === 2) hiss(t, 'highpass', 7500, 0.05, 0.16);
    else if (inBar % 2 === 1) hiss(t, 'highpass', 9000, 0.015, 0.03);
    // The bass on the off-beats, answering the kick, a sine so it stays
    // clean this low.
    if (inBar % 4 === 2) note(t, c.root, 0.24, beat() * 0.4, 'sine');
    // The chord as piano stabs: on its change and syncopated after.
    if (inBar % 8 === 0 || inBar % 8 === 3 || inBar % 8 === 6) {
      stab(t, c.notes, 0.04, 0.18, 1600, inBar % 8 === 6 ? 0.25 : 0, 'triangle');
    }
  },
};

// Chords by name, voiced around middle C, with a bass root below.
const CH: Record<string, { notes: number[]; root: number }> = {
  D: { notes: [57, 62, 66, 69], root: 38 },
  A: { notes: [57, 61, 64, 69], root: 45 },
  Bm: { notes: [59, 62, 66, 71], root: 35 },
  'F#m': { notes: [57, 61, 66, 69], root: 42 },
  G: { notes: [55, 59, 62, 67], root: 43 },
};

const TRACKS: Record<string, Track> = { lofi, house, dancehall };
const NAMES = Object.keys(TRACKS);

// The tracks in turn, always opening with lo-fi.
function trackOrder() {
  return NAMES.map((n) => TRACKS[n]);
}

const easeInOut = (x: number) => 0.5 - Math.cos(Math.PI * x) / 2;
const nextAfter = (tr: Track) => order[(order.indexOf(tr) + 1) % order.length];
const notify = () => window.dispatchEvent(new CustomEvent('chillbeat-state'));

// Moves the blend on to step s and says what's playing there. Blends start
// on a bar: one asked for, or the next track once this one has had its
// solo bars (unless auto is off).
function mixAt(s: number) {
  // Cut mode: a track asked for takes over on the very next step, and the
  // automatic change cuts on the bar, tempo and all.
  if (!blending) {
    const next =
      requested && requested !== current
        ? requested
        : auto && s >= nextAuto && s % 16 === 0
          ? nextAfter(current)
          : null;
    if (next || target) {
      current = next ?? target!;
      target = null;
      nextAuto = s - (s % 16) + SOLO_BARS * 16;
      notify();
    }
    requested = null;
    lastMix = 0;
    return { from: current, to: current, mix: 0 };
  }
  if (!target && s % 16 === 0) {
    if (requested && requested !== current) target = requested;
    else if (auto && s >= nextAuto) target = nextAfter(current);
    requested = null;
    if (target) {
      blendStart = s;
      notify();
    }
  }
  let mix = 0;
  if (target) {
    mix = (s - blendStart) / (BLEND_BARS * 16);
    if (mix >= 1) {
      current = target;
      target = null;
      nextAuto = s + SOLO_BARS * 16;
      mix = 0;
      notify();
    }
  }
  lastMix = mix;
  return { from: current, to: target ?? current, mix: easeInOut(mix) };
}

function playStep(s: number, t: number, from: Track, to: Track, mix: number) {
  const inBar = s % 16;
  const bar = Math.floor(s / 16);
  // Equal-power crossfade, so the blend doesn't dip in the middle.
  const levels = new Map([
    [from, Math.cos((mix * Math.PI) / 2)],
    [to, Math.sin((mix * Math.PI) / 2)],
  ]);
  if (from === to) levels.set(from, 1);
  for (const [tr, bus] of buses) {
    const level = levels.get(tr) ?? 0;
    bus.gain.setValueAtTime(level, t);
    if (level > 0.01) {
      out = bus;
      outLevel = level;
      tr.play(inBar, bar, t);
    }
  }
  if (inBar % 4 === 0) announce(s / 4, t, beatSec);
}

// Fires the beat's event when it actually sounds (scheduled time plus the
// output's latency), not when it's scheduled. The first one also turns the
// dancing on, so the moves start on the downbeat.
const pending = new Set<number>();
// It also sets --beat, which follows the tempo through a blend.
function announce(beat: number, t: number, sec: number) {
  const id = window.setTimeout(
    () => {
      pending.delete(id);
      const root = document.documentElement;
      root.style.setProperty('--beat', `${sec.toFixed(4)}s`);
      if (beat === 0) root.classList.add('beat-on');
      window.dispatchEvent(new CustomEvent('chillbeat', { detail: { beat, beatMs: sec * 1000 } }));
    },
    Math.max(0, (t - ctx!.currentTime + ((ctx as any).outputLatency || 0)) * 1000),
  );
  pending.add(id);
}

function schedule() {
  if (!ctx) return;
  while (nextTime < ctx.currentTime + LOOKAHEAD) {
    // Tempo and swing glide with the blend, a step at a time.
    const { from, to, mix } = mixAt(step);
    beatSec = 60 / (from.bpm + (to.bpm - from.bpm) * mix);
    const swingShare = from.swing + (to.swing - from.swing) * mix;
    const stepSec = beatSec / 4;
    if (step % 4 === 0) echoDelay!.delayTime.linearRampToValueAtTime(beatSec * 0.75, nextTime);
    const swing = step % 2 === 1 ? stepSec * swingShare : 0;
    playStep(step, nextTime + swing, from, to, mix);
    nextTime += stepSec;
    step++;
  }
}

// For the test player.
const soundOn = () => {
  try {
    return localStorage.getItem('user-sound') === 'on';
  } catch {
    return false;
  }
};

export function getBeatState() {
  return {
    sound: soundOn(),
    playing,
    tracks: NAMES.map((n) => ({ name: n, label: TRACKS[n].label, bpm: TRACKS[n].bpm })),
    current: playing ? current?.name : null,
    target: playing ? (target?.name ?? requested?.name ?? null) : null,
    mix: lastMix,
    bpm: Math.round(60 / beatSec),
    bar: Math.floor(step / 16),
    auto,
    blending,
  };
}

// Changes to a track: blending from the next bar (or after the blend under
// way), or cutting over on the next step.
export function blendTo(name: string) {
  if (!TRACKS[name]) return;
  requested = TRACKS[name];
  notify();
}

// Blend (the default) crossfades and glides the tempo over BLEND_BARS; off,
// a change cuts straight over on the next step.
export function setBlend(on: boolean) {
  blending = on;
  notify();
}

// Auto off holds the current track, for listening to one on its own.
export function setAuto(on: boolean) {
  auto = on;
  if (on && current) nextAuto = step - (step % 16) + 16;
  notify();
}

export function startBeat() {
  if (playing || !setup()) return;
  playing = true;
  paused = false;
  order = trackOrder();
  if (!buses.size) {
    for (const tr of order) {
      const bus = ctx!.createGain();
      bus.gain.value = 0;
      bus.connect(master!);
      buses.set(tr, bus);
    }
  }
  current = order[0];
  target = null;
  requested = null;
  nextAuto = SOLO_BARS * 16;
  beatSec = 60 / current.bpm;
  // The echo repeats on the dotted eighth.
  echoDelay!.delayTime.value = beatSec * 0.75;
  if (ctx!.state === 'suspended') ctx!.resume();
  const now = ctx!.currentTime;
  master!.gain.cancelScheduledValues(now);
  master!.gain.setValueAtTime(master!.gain.value, now);
  master!.gain.linearRampToValueAtTime(0.32, now + 1.5);
  step = 0;
  nextTime = now + 0.1;
  schedule();
  timer = window.setInterval(schedule, TICK_MS);

  document.documentElement.style.setProperty('--beat', `${beatSec.toFixed(4)}s`);
  notify();
}

// Sound on or off, for the footer's toggle and the player's close button.
// Off stops the beat (and the player goes with it); on starts it.
export function setSound(on: boolean) {
  try {
    localStorage.setItem('user-sound', on ? 'on' : 'off');
  } catch {
    // ignore
  }
  if (on) startBeat();
  else stopBeat();
  notify();
}

// The player's Stop: the music stops but the sound stays on, so the player
// stays, and the beat doesn't start again on its own when the tab comes back.
export function pauseBeat() {
  stopBeat();
  paused = true;
  notify();
}

export function stopBeat() {
  if (!playing) return;
  playing = false;
  if (timer !== null) clearInterval(timer);
  timer = null;
  pending.forEach((id) => clearTimeout(id));
  pending.clear();
  window.dispatchEvent(new CustomEvent('chillbeat-stop'));
  document.documentElement.classList.remove('beat-on');
  notify();
  if (ctx && master) {
    const now = ctx.currentTime;
    master.gain.cancelScheduledValues(now);
    master.gain.setValueAtTime(master.gain.value, now);
    master.gain.linearRampToValueAtTime(0, now + 0.4);
  }
}

// Whether the beat was started from the home page's player in this visit
// (sessionStorage, so a new tab or visit starts clear). Off the home page the
// player only shows, and the beat only picks up again, if it was.
const FROM_HOME = 'beat-from-home';
export function startedFromHome() {
  try {
    return sessionStorage.getItem(FROM_HOME) === 'on';
  } catch {
    return false;
  }
}
export function markStartedFromHome() {
  try {
    sessionStorage.setItem(FROM_HOME, 'on');
  } catch {
    // ignore
  }
}

// Sound left on from an earlier visit: browsers won't play audio until the
// visitor interacts, so the beat starts on the first tap or key press. It
// also rests while the tab is hidden.
export function initBeat() {
  if (!AudioCtx) return;
  // Not when that first press is on the test player or the footer's Sound
  // toggle: those decide for themselves, and starting here first would make
  // their click read as "stop".
  const firstGesture = (e: Event) => {
    const t = e.target as Element | null;
    if (t?.closest?.('.beat-player, [aria-label$="sounds"]')) return;
    // The home page starts paused, with the player there to press, and other
    // pages only carry on a beat started from it in this visit.
    if (window.location.pathname === '/') return;
    if (soundOn() && startedFromHome()) startBeat();
  };
  window.addEventListener('pointerdown', firstGesture, { once: true });
  window.addEventListener('keydown', firstGesture, { once: true });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopBeat();
    else if (soundOn() && ctx && !paused) startBeat();
  });
}
